import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Percent, Tag, Calendar, Building, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import useTaxRateStore from '@/stores/taxRateStore'

const TaxRateForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const {
    currentTaxRate,
    loading,
    error,
    fetchTaxRateById,
    createTaxRate,
    updateTaxRate,
    clearError,
    clearCurrentTaxRate
  } = useTaxRateStore()

  const [formData, setFormData] = useState({
    country_id: '',
    name: '',
    rate: 0,
    type: 'vat' as 'vat' | 'gst' | 'pst' | 'hst' | 'sales_tax' | 'other',
    region_id: '',
    enabled: true,
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: ''
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isEditMode && id) {
      fetchTaxRateById(id)
    }

    return () => {
      clearCurrentTaxRate()
    }
  }, [id, isEditMode])

  useEffect(() => {
    if (isEditMode && currentTaxRate) {
      setFormData({
        country_id: currentTaxRate.country_id,
        name: currentTaxRate.name,
        rate: currentTaxRate.rate,
        type: currentTaxRate.type,
        region_id: currentTaxRate.region_id || '',
        enabled: currentTaxRate.enabled,
        effective_from: currentTaxRate.effective_from?.split('T')[0] || new Date().toISOString().split('T')[0],
        effective_to: currentTaxRate.effective_to?.split('T')[0] || ''
      })
    }
  }, [currentTaxRate, isEditMode])

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.country_id.trim()) {
      errors.country_id = 'Le pays est requis'
    }

    if (!formData.name.trim()) {
      errors.name = 'Le nom est requis'
    }

    if (formData.rate < 0 || formData.rate > 100) {
      errors.rate = 'Le taux doit être entre 0 et 100'
    }

    if (!formData.effective_from) {
      errors.effective_from = 'La date d\'effet est requise'
    }

    if (formData.effective_to && new Date(formData.effective_to) < new Date(formData.effective_from)) {
      errors.effective_to = 'La date de fin doit être postérieure à la date de début'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      if (isEditMode && id) {
        await updateTaxRate(id, formData)
        alert('Taux de taxe mis à jour avec succès')
      } else {
        await createTaxRate(formData)
        alert('Taux de taxe créé avec succès')
      }
      navigate('/tax-rates')
    } catch (error: any) {
      console.error('Error saving tax rate:', error)
      // L'erreur est déjà gérée par le store
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (loading && isEditMode) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-gray-200 animate-pulse rounded" />
        <div className="h-96 w-full bg-gray-200 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode ? 'Modifier le taux de taxe' : 'Nouveau taux de taxe'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode 
              ? 'Modifiez les informations du taux de taxe' 
              : 'Créez un nouveau taux de taxe pour le système'}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/tax-rates')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Informations du taux de taxe</CardTitle>
          <CardDescription>
            Remplissez les informations du taux de taxe. Les champs marqués d'un * sont obligatoires.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Country ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Pays ID *
                  <span className="text-xs text-muted-foreground ml-2">(ID du pays associé)</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="country_id"
                    value={formData.country_id}
                    onChange={(e) => handleChange('country_id', e.target.value)}
                    placeholder="ex: 1"
                    className={`pl-9 ${formErrors.country_id ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.country_id && (
                  <p className="text-sm text-red-500">{formErrors.country_id}</p>
                )}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Nom *
                  <span className="text-xs text-muted-foreground ml-2">(ex: TVA Standard, TPS, etc.)</span>
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="TVA Standard"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>

              {/* Rate */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Taux (%) *
                  <span className="text-xs text-muted-foreground ml-2">(0-100)</span>
                </label>
                <div className="relative">
                  <Percent className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => handleChange('rate', parseFloat(e.target.value) || 0)}
                    className={`pl-9 ${formErrors.rate ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.rate && (
                  <p className="text-sm text-red-500">{formErrors.rate}</p>
                )}
              </div>

              {/* Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Type *</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value as 'vat' | 'gst' | 'pst' | 'hst' | 'sales_tax' | 'other')}
                >
                  <option value="vat">VAT (TVA)</option>
                  <option value="gst">GST</option>
                  <option value="pst">PST</option>
                  <option value="hst">HST</option>
                  <option value="sales_tax">Taxe de vente</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              {/* Region ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Région ID
                  <span className="text-xs text-muted-foreground ml-2">(ID de la région, laisser vide pour toutes)</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="region_id"
                    value={formData.region_id}
                    onChange={(e) => handleChange('region_id', e.target.value)}
                    placeholder="ex: 1 (laisser vide pour toutes)"
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Effective From */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Date d'effet *
                  <span className="text-xs text-muted-foreground ml-2">(Date à partir de laquelle le taux s'applique)</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="effective_from"
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => handleChange('effective_from', e.target.value)}
                    className={`pl-9 ${formErrors.effective_from ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.effective_from && (
                  <p className="text-sm text-red-500">{formErrors.effective_from}</p>
                )}
              </div>

              {/* Effective To */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Date de fin
                  <span className="text-xs text-muted-foreground ml-2">(Date jusqu'à laquelle le taux s'applique)</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="effective_to"
                    type="date"
                    value={formData.effective_to}
                    onChange={(e) => handleChange('effective_to', e.target.value)}
                    className={`pl-9 ${formErrors.effective_to ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.effective_to && (
                  <p className="text-sm text-red-500">{formErrors.effective_to}</p>
                )}
              </div>
            </div>

            {/* Enabled Switch */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="enabled" className="text-sm font-medium cursor-pointer">
                Taux de taxe actif
              </label>
            </div>

            {/* Preview */}
            <div className="mt-8 p-4 border rounded-lg bg-muted/50">
              <h3 className="text-lg font-semibold mb-2">Aperçu du taux de taxe</h3>
              <div className="flex items-center space-x-4">
                <Tag className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Taux de taxe créé:</p>
                  <p className="text-2xl font-bold">{formData.name} - {formData.rate}%</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Type: {formData.type} • Pays ID: {formData.country_id} • 
                    Région: {formData.region_id || 'Toutes'} • 
                    Effet: {formData.effective_from} {formData.effective_to && `jusqu'au ${formData.effective_to}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/tax-rates')}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Enregistrement...' : isEditMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default TaxRateForm
